import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X, Search } from 'lucide-react';
import { filterBySearchQuery } from '../../utils/textUtils';

export interface ComboboxOption {
  id: string | number;
  label: string;
  sublabel?: string;
  searchFields: string[]; // Fields to search on
}

interface SearchableComboboxProps {
  options: ComboboxOption[];
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  maxDisplayItems?: number;
  debounceMs?: number;
  loading?: boolean;
  onSearchChange?: (query: string) => void; // For server-side search
}

export const SearchableCombobox: React.FC<SearchableComboboxProps> = ({
  options,
  value,
  onChange,
  placeholder = '',
  disabled = false,
  required = false,
  className = '',
  maxDisplayItems = 30,
  debounceMs = 300,
  loading = false,
  onSearchChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Find selected option
  const selectedOption = useMemo(
    () => options.find((opt) => opt.id === value) || null,
    [options, value]
  );

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    const filtered = filterBySearchQuery(options, searchQuery, (opt) => opt.searchFields);
    return filtered.slice(0, maxDisplayItems);
  }, [options, searchQuery, maxDisplayItems]);

  // Display text in input
  const displayText = useMemo(() => {
    if (searchQuery) return searchQuery;
    if (selectedOption) return selectedOption.label;
    return '';
  }, [searchQuery, selectedOption]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Debounced search callback for server-side search
  const debouncedSearchChange = useCallback(
    (query: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (onSearchChange) {
        debounceTimerRef.current = setTimeout(() => {
          onSearchChange(query);
        }, debounceMs);
      }
    },
    [onSearchChange, debounceMs]
  );

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Clear selected value when user types after selection
    if (value !== null && query !== selectedOption?.label) {
      onChange(null);
    }

    debouncedSearchChange(query);
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
  };

  // Handle option select
  const handleSelect = (option: ComboboxOption) => {
    onChange(option.id);
    setSearchQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle clear
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchQuery('');
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        break;

      case 'Tab':
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input container */}
      <div
        className={`
          relative flex items-center border rounded-lg transition-colors
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-text'}
          ${isOpen ? 'border-primary-500 ring-2 ring-primary-100' : 'border-gray-300'}
          ${!disabled && 'hover:border-gray-400'}
        `}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        <Search className="w-4 h-4 text-gray-400 ml-3" />

        <input
          ref={inputRef}
          type="text"
          value={displayText}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required && !value}
          className={`
            flex-1 px-3 py-2 outline-none bg-transparent text-sm
            ${disabled ? 'cursor-not-allowed text-gray-500' : 'text-gray-900'}
          `}
          autoComplete="off"
        />

        {/* Clear button */}
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 mr-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Dropdown arrow */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`p-2 text-gray-400 ${disabled ? '' : 'hover:text-gray-600'}`}
          tabIndex={-1}
        >
          <ChevronDown
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              {t('common.loading')}
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              {t('common.noData')}
            </div>
          ) : (
            <ul ref={listRef} className="overflow-y-auto max-h-60">
              {filteredOptions.map((option, index) => (
                <li
                  key={option.id}
                  onClick={() => handleSelect(option)}
                  className={`
                    px-4 py-2 cursor-pointer transition-colors
                    ${index === highlightedIndex ? 'bg-primary-50' : 'hover:bg-gray-50'}
                    ${option.id === value ? 'bg-primary-50 border-l-2 border-primary-500' : ''}
                  `}
                >
                  <div className="text-sm font-medium text-gray-900">{option.label}</div>
                  {option.sublabel && (
                    <div className="text-xs text-gray-500 mt-0.5">{option.sublabel}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// Helper functions to create options for different entity types

export const createVehicleOptions = (vehicles: any[]): ComboboxOption[] => {
  return vehicles.map((v) => ({
    id: v.id,
    label: v.licensePlate,
    sublabel: `${v.type} • ${v.capacity} tấn`,
    searchFields: [v.licensePlate, v.type || ''],
  }));
};

export const createSupplierOptions = (suppliers: any[]): ComboboxOption[] => {
  return suppliers.map((s) => ({
    id: s.id,
    label: s.name,
    sublabel: s.phone || s.address || '',
    searchFields: [s.name, s.phone || '', s.address || ''],
  }));
};

export const createCustomerOptions = (customers: any[]): ComboboxOption[] => {
  return customers.map((c) => ({
    id: c.id,
    label: c.name,
    sublabel: c.phone || c.address || '',
    searchFields: [c.name, c.phone || '', c.address || ''],
  }));
};

export const createDriverOptions = (drivers: any[]): ComboboxOption[] => {
  return drivers.map((d) => ({
    id: d.id,
    label: d.name,
    sublabel: d.phone || d.licenseNumber || '',
    searchFields: [d.name, d.phone || '', d.licenseNumber || ''],
  }));
};

export const createTripOptions = (trips: any[]): ComboboxOption[] => {
  return trips.map((t) => ({
    id: t.id,
    label: `#${t.id} - ${t.vehicle?.licensePlate || ''}`,
    sublabel: `${new Date(t.date).toLocaleDateString('vi-VN')} • ${t.customer?.name || t.supplier?.name || ''}`,
    searchFields: [
      String(t.id),
      t.vehicle?.licensePlate || '',
      t.customer?.name || '',
      t.supplier?.name || '',
    ],
  }));
};

export const createItemOptions = (items: any[]): ComboboxOption[] => {
  return items.map((i) => ({
    id: i.id,
    label: i.name,
    sublabel: i.defaultUnit || '',
    searchFields: [i.name, i.defaultUnit || ''],
  }));
};
